export type ChatShareImportCooldownProvider = "chatgpt" | "gemini";

export const CHAT_SHARE_IMPORT_COOLDOWN_MS = 30_000;
export const CHAT_SHARE_IMPORT_COOLDOWN_SECONDS =
  CHAT_SHARE_IMPORT_COOLDOWN_MS / 1_000;

export function chatShareImportCooldownStorageKey(
  provider: ChatShareImportCooldownProvider,
) {
  return `chat-share-import-cooldown-until:${provider}`;
}

function readCooldownUntilMs(
  provider: ChatShareImportCooldownProvider,
): number | null {
  try {
    const key = chatShareImportCooldownStorageKey(provider);
    const raw = window.sessionStorage.getItem(key);

    if (!raw) {
      return null;
    }

    const until = Number(raw);

    if (!Number.isFinite(until)) {
      window.sessionStorage.removeItem(key);
      return null;
    }

    return until;
  } catch {
    return null;
  }
}

export function readChatShareImportCooldownRemainingSeconds(
  provider: ChatShareImportCooldownProvider,
  now = Date.now(),
): number | null {
  const until = readCooldownUntilMs(provider);

  if (until === null) {
    return null;
  }

  const remainingSeconds = Math.ceil((until - now) / 1_000);

  if (remainingSeconds <= 0) {
    clearChatShareImportCooldown(provider);
    return null;
  }

  return remainingSeconds;
}

export function writeChatShareImportCooldownUntil(
  provider: ChatShareImportCooldownProvider,
  epochMs: number,
) {
  try {
    window.sessionStorage.setItem(
      chatShareImportCooldownStorageKey(provider),
      String(epochMs),
    );
  } catch {
    // sessionStorage may be unavailable (private mode / quota); UI still works.
  }
}

export function clearChatShareImportCooldown(
  provider: ChatShareImportCooldownProvider,
) {
  try {
    window.sessionStorage.removeItem(
      chatShareImportCooldownStorageKey(provider),
    );
  } catch {
    // Ignore storage failures when clearing.
  }
}

export function startChatShareImportCooldown(
  provider: ChatShareImportCooldownProvider,
  durationSeconds: number,
  now = Date.now(),
) {
  const seconds = Math.max(1, Math.ceil(durationSeconds));
  writeChatShareImportCooldownUntil(provider, now + seconds * 1_000);
  return seconds;
}
